import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { BookOpen, Send, X } from "lucide-react";
import { toast } from "./ui/use-toast";

const FloatingLeadCapture = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Show on neighborhood, blog, and free-guides pages
    const shouldShow = location.pathname.includes('/neighborhood/') || 
                       location.pathname.includes('/blog') || 
                       location.pathname.includes('/free-guides');
    
    if (shouldShow) {
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setIsOpen(false);
    }
  }, [location]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // CRM Tracking
    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "Floating Lead Capture",
      formData: {
        first_name: formData.get('name'),
        email: formData.get('email'),
      },
      formLabels: {
        first_name: "First Name",
        email: "Email Address",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
      locationId: "M7JmlFFXdtFNLwyfY61D",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: { "Content-Type": "application/json", version: "2021-07-28" },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    toast({ title: "Success!", description: "Your free guide is on its way to your inbox." });
    setIsOpen(false);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50 md:bottom-8 md:right-8 flex flex-col items-end">
      {isOpen ? (
        <div className="bg-card border shadow-xl rounded-2xl p-6 w-80 animate-in slide-in-from-bottom-4 fade-in">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2 text-primary font-bold">
              <BookOpen className="h-5 w-5" />
              <span>Free Cleaning Guide</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Get our exclusive "15-Minute Daily Tidy Routine" sent straight to your inbox.
          </p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input name="name" placeholder="First Name" required className="bg-background" />
            <Input name="email" type="email" placeholder="Email Address" required className="bg-background" />
            <Button type="submit" className="w-full">
              <Send className="mr-2 h-4 w-4" />
              Send My Guide
            </Button>
          </form>
        </div>
      ) : (
        <Button 
          onClick={() => setIsOpen(true)}
          className="rounded-full h-14 px-6 shadow-xl hover:scale-105 transition-transform"
          size="lg"
        >
          <BookOpen className="mr-2 h-5 w-5" />
          Get Free Guide
        </Button>
      )}
    </div>
  );
};

export default FloatingLeadCapture;
